import React, { useState, useEffect, useRef } from 'react';
import './Requests.css';
import { useUser } from '../../auth/AuthPage/UserContext';
import { useNotifications } from '../../../contexts/NotificationContext';
import { Link, useNavigate } from 'react-router-dom';
import { getProfilePhotoSrc, PLACEHOLDER_DATA_URI } from '../../../utils/profilePhoto';
import TradeSides, { useCardInfo } from './TradeSides';
import LoginGate from '../../common/LoginGate/LoginGate';
import AvatarFrame from '../../common/AvatarFrame/AvatarFrame';
import Icon from '../../common/Icon/Icon';

/**
 * THE TRADE DASHBOARD - the four old sections (New Trade / Offers /
 * Active / History) collapsed into one page, grouped by whose move
 * it is:
 *   Your move       - incoming offers + shipping steps waiting on YOU
 *   Waiting on them - your outgoing offers + shipping steps on THEM
 *   History         - finished and cancelled trades
 * Tickets speak the match-panel language from Find Traders: the
 * trade itself as "You get" / "You give" thumbnail strips with a
 * ~$ value per side (server-enriched). New trades start where trades
 * actually start: a binder, a search result, or a match panel.
 */
function Requests() {
    const { user } = useUser();
    const navigate = useNavigate();
    const { hasTradeNotification, markTradeAsRead } = useNotifications();

    const [trades, setTrades] = useState(null); // null = loading
    const [error, setError] = useState(null);
    const [shipStages, setShipStages] = useState({});
    const [deletingRequest, setDeletingRequest] = useState(null);
    const [confirmTarget, setConfirmTarget] = useState(null); // { tradeId, verb }

    // Other-party info per trade (avatar + username)
    const userInfoCache = useRef({});
    const [userInfoState, setUserInfoState] = useState({});

    // The 15-second poller below captures fetchAll from the FIRST render,
    // where `trades` was still null, so reading `trades` inside it would
    // always say "nothing loaded yet". This ref always holds the current
    // value, so a failed background check leaves the page alone.
    const tradesRef = useRef(trades);
    useEffect(() => { tradesRef.current = trades; }, [trades]);

    const fetchAll = async (silent = false) => {
        try {
            if (!silent && tradesRef.current === null) setError(null);
            const [tradesRes, stagesRes] = await Promise.all([
                fetch('/api/trade-requests?type=dashboard', { credentials: 'include' }),
                fetch('/api/trade-requests/shipping-attention', { credentials: 'include' }),
            ]);
            if (!tradesRes.ok) throw new Error('Failed to fetch trades');
            const data = await tradesRes.json();
            setTrades(data.requests || []);
            if (stagesRes.ok) {
                const stages = await stagesRes.json();
                setShipStages(Object.fromEntries((stages.items || []).map(i => [i.tradeId, i])));
            }
        } catch (err) {
            // Only the very first load has nothing to fall back on; a
            // background check that fails is simply retried next tick
            if (tradesRef.current === null) setError(err.message || 'Failed to load trades');
        }
    };

    useEffect(() => {
        if (user) fetchAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    // Live updates: the other party's moves (accepts, counters,
    // shipping steps) show without a manual refresh
    useEffect(() => {
        if (!user) return;
        const id = setInterval(() => {
            if (!document.hidden) fetchAll(true);
        }, 15000);
        const onFocus = () => fetchAll(true);
        window.addEventListener('focus', onFocus);
        return () => {
            clearInterval(id);
            window.removeEventListener('focus', onFocus);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    // Fetch the other party's public info for every visible trade
    useEffect(() => {
        if (!user || !trades?.length) return;
        const ids = Array.from(new Set(trades.map(r =>
            r.ownerId === user.id ? r.otherId : r.ownerId
        ))).filter(Boolean);
        ids.forEach(async (userId) => {
            if (userInfoCache.current[userId]) return;
            try {
                const res = await fetch(`/api/users/${userId}/public`);
                if (res.ok) {
                    const data = await res.json();
                    userInfoCache.current[userId] = data.user;
                    setUserInfoState(prev => ({ ...prev, [userId]: data.user }));
                }
            } catch { }
        });
    }, [trades, user]);

    // Long trade notes clamp to a few lines; click to read it all
    const [openNotes, setOpenNotes] = useState({});

    // The site's standard card info window, on every thumbnail
    const { openCardInfo, cardInfoElement } = useCardInfo();

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    };

    // ---- actions ----
    const confirmRemove = async () => {
        if (!confirmTarget) return;
        try {
            setDeletingRequest(confirmTarget.tradeId);
            const response = await fetch(`/api/trade-requests/${confirmTarget.tradeId}`, {
                method: 'DELETE',
                credentials: 'include'
            });
            if (!response.ok) throw new Error(`Failed to ${confirmTarget.verb} the offer`);
            setTrades(prev => prev.filter(r => r.tradeId !== confirmTarget.tradeId));
        } catch (err) {
            setError(err.message || 'Failed to remove the offer');
        } finally {
            setDeletingRequest(null);
            setConfirmTarget(null);
        }
    };

    const handleAcceptRequest = async (request) => {
        try {
            const response = await fetch(`/api/trade-requests/${request.tradeId}/status`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ status: 'accepted' })
            });
            if (!response.ok) throw new Error('Failed to accept request');
            fetchAll(true);
        } catch (err) {
            setError(err.message || 'Failed to accept request');
        }
    };

    const handleCounterOffer = (request) => {
        navigate('/trade', {
            state: {
                requestData: request,
                ownerId: request.ownerId,
                isCounterOffer: true
            }
        });
    };

    if (!user) {
        return <LoginGate title="Trades" message="Log in to view and manage your trades." />;
    }

    // ---- grouping: whose move is it? ----
    const list = trades || [];
    const incoming = list.filter(r => r.status === 'pending' && r.otherId === user.id);
    const outgoing = list.filter(r => r.status === 'pending' && r.ownerId === user.id);
    const active = list.filter(r => ['accepted', 'processing'].includes(r.status));
    const history = list.filter(r => ['finished', 'cancelled'].includes(r.status));
    const activeYourTurn = active.filter(r => shipStages[r.tradeId]?.actionNeeded);
    const activeWaiting = active.filter(r => !shipStages[r.tradeId]?.actionNeeded);

    const yourMove = [
        ...incoming.map(r => ({ r, kind: 'incoming' })),
        ...activeYourTurn.map(r => ({ r, kind: 'active' })),
    ];
    const waiting = [
        ...outgoing.map(r => ({ r, kind: 'outgoing' })),
        ...activeWaiting.map(r => ({ r, kind: 'active' })),
    ];

    // ---- ticket pieces ----
    const kindChip = (request, kind) => {
        if (kind === 'incoming') return <span className="tk-chip gold">Offer to you</span>;
        if (kind === 'outgoing') return <span className="tk-chip">Your offer</span>;
        if (kind === 'active') {
            const stage = shipStages[request.tradeId];
            return (
                <span className={`tk-chip ${stage?.actionNeeded ? 'gold' : ''}`}>
                    {stage?.actionNeeded ? 'Your turn' : 'In progress'}
                </span>
            );
        }
        if (request.status === 'cancelled') return <span className="tk-chip bad">Cancelled</span>;
        return <span className="tk-chip done">Finished</span>;
    };

    const renderTicket = ({ r: request, kind }) => {
        // My side of the trade is otherTrade when I'm the other party
        const iAmOther = request.otherId === user.id;
        const myCards = iAmOther ? request.otherTrade : request.ownerTrade;
        const theirCards = iAmOther ? request.ownerTrade : request.otherTrade;
        const otherPersonId = request.ownerId === user.id ? request.otherId : request.ownerId;
        const partner = otherPersonId ? userInfoState[otherPersonId] : null;
        const unread = hasTradeNotification(request.tradeId);
        const stage = shipStages[request.tradeId];

        return (
            <article
                key={`${kind}-${request.tradeId}`}
                className={`tk${unread ? ' unread' : ''}`}
                onClick={() => { if (unread) markTradeAsRead(request.tradeId); }}
            >
                <header className="tk-head">
                    <button
                        className="tk-who"
                        onClick={() => otherPersonId && navigate(`/profile/${otherPersonId}`)}
                    >
                        <AvatarFrame user={partner}>
                            <img
                                src={partner ? getProfilePhotoSrc(partner) : PLACEHOLDER_DATA_URI}
                                alt={partner?.username || 'trader'}
                                onError={e => { e.target.onerror = null; e.target.src = PLACEHOLDER_DATA_URI; }}
                            />
                        </AvatarFrame>
                        <span className="tk-who-text">
                            <span className="tk-name">
                                {partner?.username || '…'}
                                {unread && <span className="tk-dot" aria-label="new activity" />}
                            </span>
                            <span className="tk-meta">
                                {formatDate(request.created_at || new Date())}
                                {request.trade_code && ` · ${request.trade_code}`}
                            </span>
                        </span>
                    </button>
                    {kindChip(request, kind)}
                </header>

                <TradeSides getCards={theirCards} giveCards={myCards} onCardClick={openCardInfo} />

                {request.note && (() => {
                    const longNote = request.note.length > 140;
                    const open = !!openNotes[request.tradeId];
                    return (
                        <p
                            className={`tk-note${longNote ? ' clickable' : ''}`}
                            title={longNote ? (open ? 'Show less' : 'Show the full note') : undefined}
                            onClick={longNote
                                ? () => setOpenNotes(o => ({ ...o, [request.tradeId]: !o[request.tradeId] }))
                                : undefined}
                        >
                            <span className="tk-note-label">Note</span>
                            <span className={`tk-note-text${longNote && !open ? ' clamp' : ''}`}>
                                “{request.note}”
                            </span>
                        </p>
                    );
                })()}

                {kind === 'active' && (
                    <div className="tk-stage">
                        <ol className="stage-dots">
                            {['Decide', 'Setup', 'Document', 'Ship'].map((label, i) => {
                                const n = i + 1;
                                const cur = stage?.step || 1;
                                return (
                                    <li key={label} className={n < cur ? 'done' : n === cur ? 'current' : ''}>
                                        <span className="stage-dot">{n < cur ? '✓' : n}</span>
                                        {label}
                                    </li>
                                );
                            })}
                        </ol>
                        {stage && (
                            <span className={`stage-turn ${stage.actionNeeded ? 'yours' : 'waiting'}`}>
                                {stage.label}
                            </span>
                        )}
                    </div>
                )}

                <footer className="tk-actions">
                    {otherPersonId && (
                        <button
                            className="tk-btn ghost tk-msg"
                            onClick={() => navigate(`/messages?to=${otherPersonId}`)}
                            title={`Message ${partner?.username || 'them'}`}
                        >
                            <Icon name="chat" /> Message
                        </button>
                    )}
                    {kind === 'incoming' && (
                        <>
                            <button
                                className="tk-btn danger"
                                onClick={() => setConfirmTarget({ tradeId: request.tradeId, verb: 'decline' })}
                                disabled={deletingRequest === request.tradeId}
                            >
                                Decline
                            </button>
                            <button
                                className="tk-btn ghost"
                                onClick={() => handleCounterOffer(request)}
                                disabled={deletingRequest === request.tradeId}
                            >
                                Counter
                            </button>
                            <button
                                className="tk-btn gold"
                                onClick={() => handleAcceptRequest(request)}
                                disabled={deletingRequest === request.tradeId}
                            >
                                Accept
                            </button>
                        </>
                    )}
                    {kind === 'outgoing' && (
                        <button
                            className="tk-btn danger"
                            onClick={() => setConfirmTarget({ tradeId: request.tradeId, verb: 'withdraw' })}
                            disabled={deletingRequest === request.tradeId}
                        >
                            {deletingRequest === request.tradeId ? 'Withdrawing…' : 'Withdraw'}
                        </button>
                    )}
                    {kind === 'active' && (
                        <button
                            className={`tk-btn ${stage?.actionNeeded ? 'gold' : 'ghost'}`}
                            onClick={() => navigate(`/shipping/${request.tradeId}`)}
                        >
                            Open shipping
                        </button>
                    )}
                </footer>
            </article>
        );
    };

    // History is the archive: always on screen, one slim row per trade
    // (who, when, how it ended) - the full record is a click away
    const renderHistoryRow = (request) => {
        const otherPersonId = request.ownerId === user.id ? request.otherId : request.ownerId;
        const partner = otherPersonId ? userInfoState[otherPersonId] : null;
        return (
            <li key={`h-${request.tradeId}`} className="hist-row">
                <button
                    className="hist-open"
                    onClick={() => navigate(`/trades/${request.tradeId}/history`)}
                    title="Open the full record"
                >
                    <AvatarFrame user={partner}>
                        <img
                            src={partner ? getProfilePhotoSrc(partner) : PLACEHOLDER_DATA_URI}
                            alt=""
                            onError={e => { e.target.onerror = null; e.target.src = PLACEHOLDER_DATA_URI; }}
                        />
                    </AvatarFrame>
                    <span className="hist-name">{partner?.username || '…'}</span>
                    <span className="hist-meta">
                        {formatDate(request.created_at || new Date())}
                        {request.trade_code && ` · ${request.trade_code}`}
                    </span>
                    {request.status === 'cancelled' && request.cancel_reason && (
                        <span className="hist-reason" title={request.cancel_reason}>{request.cancel_reason}</span>
                    )}
                    {request.status === 'cancelled'
                        ? <span className="tk-chip bad">Cancelled</span>
                        : <span className="tk-chip done">Finished</span>}
                    <span className="hist-go" aria-hidden="true">→</span>
                </button>
            </li>
        );
    };

    const loading = trades === null && !error;

    return (
        <div className="requests-container">
            <header className="page-section-head">
                <h1>Trades</h1>
                {trades !== null && (
                    <p className="dash-summary">
                        {yourMove.length === 0
                            ? 'Nothing needs you right now'
                            : `${yourMove.length} need${yourMove.length === 1 ? 's' : ''} your move`}
                        {waiting.length > 0 && ` · ${waiting.length} waiting on them`}
                    </p>
                )}
            </header>

            {loading && <div className="requests-placeholder">Loading your trades&hellip;</div>}
            {error && (
                <div className="requests-placeholder" style={{ color: 'var(--danger)' }}>{error}</div>
            )}

            {!loading && !error && list.length === 0 && (
                <div className="requests-placeholder">
                    No trades yet. Find a card on the{' '}
                    <Link to="/search">search page</Link> or a partner on the{' '}
                    <Link to="/traders">traders page</Link> and send your first offer.
                </div>
            )}

            {!loading && !error && list.length > 0 && (
                <>
                    <section className="dash-sec">
                        <div className="dash-sec-head">
                            <h2>Your move</h2>
                            {yourMove.length > 0 && <span className="dash-sec-count gold">{yourMove.length}</span>}
                        </div>
                        {yourMove.length === 0 ? (
                            <div className="dash-sec-empty">Nothing needs you right now.</div>
                        ) : (
                            <div className="tk-grid">{yourMove.map(renderTicket)}</div>
                        )}
                    </section>

                    {waiting.length > 0 && (
                        <section className="dash-sec">
                            <div className="dash-sec-head">
                                <h2>Waiting on them</h2>
                                <span className="dash-sec-count">{waiting.length}</span>
                            </div>
                            <div className="tk-grid">{waiting.map(renderTicket)}</div>
                        </section>
                    )}

                    {history.length > 0 && (
                        <section className="dash-sec">
                            <div className="dash-sec-head">
                                <h2>History</h2>
                                <span className="dash-sec-count">{history.length}</span>
                            </div>
                            <ul className="hist-list">
                                {[...history]
                                    .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))
                                    .map(renderHistoryRow)}
                            </ul>
                        </section>
                    )}
                </>
            )}

            {cardInfoElement}

            {confirmTarget && (
                <div className="confirm-modal-overlay" onClick={() => setConfirmTarget(null)}>
                    <div className="confirm-modal" onClick={e => e.stopPropagation()}>
                        <div className="confirm-modal-header">
                            <h3>{confirmTarget.verb === 'decline' ? 'Decline Offer' : 'Withdraw Offer'}</h3>
                        </div>
                        <div className="confirm-modal-content">
                            {confirmTarget.verb === 'decline'
                                ? 'Decline this offer? The other trader will be notified.'
                                : 'Withdraw this offer? This cannot be undone.'}
                        </div>
                        <div className="confirm-modal-actions">
                            <button
                                className="confirm-modal-cancel"
                                onClick={() => setConfirmTarget(null)}
                                disabled={deletingRequest}
                            >
                                Cancel
                            </button>
                            <button
                                className="confirm-modal-confirm"
                                onClick={confirmRemove}
                                disabled={deletingRequest}
                            >
                                {deletingRequest
                                    ? 'Working…'
                                    : confirmTarget.verb === 'decline' ? 'Decline' : 'Withdraw'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default Requests;
